<template>
  <div class="personal-application-page">
    <div v-infinite-scroll="onLoad" :infinite-scroll-disabled="disabledLoadDate" class="application-list">
      <div v-for="item in showApplicationList" class="application-item" :key="item.id">
        <div class="company-info">
          <img class="logo" :src="$DjangoMediaUrl(item.company.logo)" />
          <div class="detail-info">
            <div class="company-name info" @click="handleClickCompany(item.company.id)">{{ item.companyName }}</div>
            <div class="job-name info" @click="handleClickJob(item.company.id, item.job.short_id)">{{ item.jobTitle }}</div>
            <div class="date info">{{ item.date }}</div>
          </div>
        </div>
        <div class="btn-group">
          <i v-if="item.need_invite_survey || applicationStatus === INCOMPLETE_APPLICATION" class="continue-icon iconfont iconapplication" @click="handleContinueApply(item)"></i>
          <van-popover
            v-model="showRevokePopover[item.id]"
            class="popover"
            placement="left"
            trigger="click"
          >
            <div class="revoke-btn-group">
              <div v-if="item.can_revoke_single_application_agreement" class="item" @click="handleRevokeApplication('one', item)">{{ $t(`${i18nPrefix}.btn.revokeThis`) }}</div>
              <div v-if="item.can_revoke_agreement" class="item" @click="handleRevokeApplication('all', item)">{{ $t(`${i18nPrefix}.btn.revokeAll`) }}</div>
            </div>
            <template #reference>
              <i class="revoke-icon iconfont icondelete"></i>
            </template>
          </van-popover>
        </div>
      </div>
    </div>
    <van-dialog
      v-rect-loading="removeDialog.loading"
      v-model="removeDialog.show"
      class="remove-dialog"
      show-cancel-button
      :title="removeDialog.title"
      :confirmButtonText="$t(`${i18nPrefix}.btn.yes`)"
      :cancelButtonText="$t(`${i18nPrefix}.btn.no`)"
      @confirm="confirmRevokeApplication"
    >
    </van-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { COMPLETED_APPLICATION, INCOMPLETE_APPLICATION } from '@/constant/application'
import { ROOT_COMPANY_ID } from '@/constant/company'
import { mapActions } from 'vuex'
import { addI18nProperty } from '@/i18n'

export default {
  data () {
    return {
      disabledLoadDate: false,
      applicationList: [],
      showApplicationList: [],
      COMPLETED_APPLICATION,
      INCOMPLETE_APPLICATION,
      i18nPrefix: 'personal.applications',
      pagination: {
        page: 0,
        pageSize: 10,
        total: 0
      },
      removeDialog: {
        show: false,
        loading: false,
        title: null,
        row: null,
        isRevokeAll: false
      },
      finishedJobs: [],
      pendingJobs: [],
      hasInitData: false,
      showRevokePopover: {},
      showContinueApplyPopover: {}
    }
  },
  computed: {
    pendingJobsCount () {
      const count = this.pendingJobs.length
      if (!count) {
        return null
      }
      return count
    },
    companyId () {
      return this.$route.query.company_id
    },
    applicationStatus () {
      return this.$route.meta.applicationStatus
    }
  },
  created () {
    this.initData()
  },
  mounted () {
  },
  methods: {
    ...mapActions('personal', ['getPersonalApplication', 'personalRevokeApplication', 'personalRevokeAllApplication']),
    onLoad () {
      if (!this.hasInitData) {
        return
      }
      this.$eventBus.$emit('showLoading', true)
      const pagination = this.pagination
      this.disabledLoadDate = true

      const startIndex = pagination.page * pagination.pageSize
      this.showApplicationList.push(
        ..._.slice(this.applicationList, startIndex, startIndex + pagination.pageSize)
      )
      this.pagination.page++
      if (pagination.page * pagination.pageSize < pagination.total) {
        this.disabledLoadDate = false
      }
      this.$eventBus.$emit('showLoading', false)
    },
    async initData () {
      this.$eventBus.$emit('showLoading', true)
      this.pagination.page = 0
      this.pagination.total = 0
      this.showApplicationList = []
      try {
        await this.loadCandidateApplication()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadCandidateApplication () {
      const resp = await this.getPersonalApplication({
        company_id: this.companyId
      })
      if (resp) {
        const pendingJobs = this.formatApplicationData(resp.pending_jobs)
        const finishedJobs = this.formatApplicationData(resp.finished_jobs)
        this.finishedJobs = _.orderBy(finishedJobs, 'updated_time', 'desc')
        this.pendingJobs = _.orderBy(pendingJobs, 'updated_time', 'desc')
        if (this.applicationStatus === COMPLETED_APPLICATION) {
          this.applicationList = this.finishedJobs
        } else {
          this.applicationList = this.pendingJobs
        }
        this.pagination.total = this.applicationList.length
        this.hasInitData = true
        this.onLoad()
      }
    },
    async handlePaginationChange (pagination) {
      this.pagination = pagination
    },
    handleRevokeApplication (command, application) {
      if (command === 'one') {
        this.removeDialog.title = this.$t(`${this.i18nPrefix}.label.removeOneApplication`, { jobTitle: application.jobTitle })
        this.removeDialog.isRevokeAll = false
      } else if (command === 'all') {
        this.removeDialog.title = this.$t(`${this.i18nPrefix}.label.removeAllApplication`, { companyName: application.companyName })
        this.removeDialog.isRevokeAll = true
      }
      this.removeDialog.show = true
      this.removeDialog.row = application
    },
    async confirmRevokeApplication () {
      this.removeDialog.loading = true
      const row = this.removeDialog.row
      try {
        let resp
        if (this.removeDialog.isRevokeAll) {
          resp = await this.personalRevokeAllApplication({ company_id: row.company.id })
        } else {
          resp = await this.personalRevokeApplication({ jobId: row.job.id })
        }
        if (resp === 'success') {
          this.$message.success(this.$t(`${this.i18nPrefix}.message.revokeSuccess`))
        }
      } finally {
        this.removeDialog.loading = false
        this.removeDialog.show = false
      }
      await this.initData()
      this.$eventBus.$emit('changeHomePageInfo')
    },
    handleContinueApply (application) {
      if (this.applicationStatus === INCOMPLETE_APPLICATION) {
        window.location.href = `/mobile-web-mini-resume/${application.job.id}`
      } else {
        window.location.href = `/candidate-invite-job-survey/?jobID=${application.job.id}&back=${this.$route.fullPath}`
      }
    },
    formatApplicationData (data) {
      return _.map(data, item => {
        if (item.company.root_company_id === ROOT_COMPANY_ID.SANOFI) {
          addI18nProperty(item, 'companyName', 'company.root_company_cn_name', 'company.root_company_name')
        } else {
          addI18nProperty(item, 'companyName', 'company.cn_name', 'company.name')
        }
        addI18nProperty(item, 'jobTitle', 'job.cn_title', 'job.title')
        item.date = moment(item.updated_time).format('YYYY-MM-DD')
        return item
      })
    },
    handleClickCompany (companyId) {
      this.$router.push(`/recruiting/company-detail/${companyId}`)
    },
    handleClickJob (companyId, jobShortId) {
      this.$router.push({ path: `/job-description/${jobShortId}/`, query: { company_id: companyId } })
    }
  },
  watch: {
    applicationStatus: {
      handler () {
        if (this.applicationStatus === COMPLETED_APPLICATION) {
          this.applicationList = this.finishedJobs
        } else {
          this.applicationList = this.pendingJobs
        }
        this.showApplicationList = []
        this.pagination.page = 0
        this.pagination.total = this.applicationList.length
        this.disabledLoadDate = false
        this.onLoad()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.personal-application-page {
  .tabs {
    ::v-deep {
      .van-info {
        right: -6px;
      }

      .van-tabs__line {
        background-color: var(--custom-color, #0096cc);
      }
    }
  }

  .application-list {
    .application-item {
      background-color: #fff;
      border-radius: 10px;
      padding: 10px;
      margin-bottom: 24px;

      .company-info {
        display: flex;
        overflow: hidden;
        align-items: center;

        .logo {
          flex-shrink: 0;
          width: 60px;
          height: 60px;
          margin-right: 12px;
        }

        .detail-info {
          overflow: hidden;

          .info {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            line-height: 26px;
            font-size: 14px;
          }
        }
      }

      .btn-group {
        text-align: right;
        position: relative;
        top: -6px;
        height: 8px;

        .popover {
          .revoke-icon {
            font-size: 14px;
          }
        }

        .continue-icon {
          font-size: 14px;
          margin-right: 10px;
        }
      }
    }
  }

  .remove-dialog {
    ::v-deep {
      .van-dialog__header {
        padding: 24px 12px;
      }
    }
  }
}

.revoke-btn-group {
  display: flex;
  flex-direction: column;

  .item {
    padding: 6px 12px;
    line-height: 18px;
    font-size: 14px;
  }
}
</style>
